// src/features/projects/weekly/MyWeeklyUpdate.jsx
//
// PMO Slice C — "My weekly update". One reusable surface opened from project
// detail ("Post / my update") and from Weekly Overview. Always targets the
// SERVER's current UTC Monday (getWeeklyWeeks().currentWeek), never an archive
// week selected elsewhere. Population = the caller's /my/assignments only.
//
// Write safety:
//   - every write for a project is serialised through one queue (in-order);
//   - note appends are single-flight per track (blur + Enter post once);
//   - an append with no HTTP answer is "ambiguous": the draft is kept and
//     locked until a refetch shows whether it landed, never blindly retried;
//   - stale responses are dropped via a generation counter on reload;
//   - server messages render verbatim next to the control that failed.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import {
  getWeeklyWeeks, getWeeklyOverview, getMyAssignments, getProjectWeek, getProjectParticipants,
  postTrackUpdate, putTrackStatus, putWeeklySummary, postLineCorrection, postUpdateComplete, isMondayIso,
} from "../../../data/projectWeeklyClient.js";
import {
  TRACKS, TRACK_STATUS_META, OVERALL_STATUS_META, statusForTrack, resolveLine, weekLabel, fmtTimestamp,
} from "./weeklyModel.js";
import {
  WRITE_KIND, groupAssignments, myPersonId, editableTrackKeys, canEditSummary,
  suggestOverall, draftFromTracks, classifyWriteError, createSerialQueue,
  createAppendGuard, findLandedLine, isCompletedBy,
} from "./myUpdateModel.js";
import { btn, btnPrimary, inputStyle, muted, withDisabled } from "./weeklyUi.js";
import { weeklyText } from "../../../lib/weeklyRichText.js";
import { showToast } from "../../../components/toastService.jsx";
import { CarryForward } from "./PriorSection.jsx";
import { WeeklyRichEditor } from "./WeeklyRichEditor.jsx";

const SUMMARY_DEBOUNCE_MS = 900;
const TRACK_STATUS_ORDER = ["on_track", "watch", "blocked"];
const OVERALL_ORDER = ["on_track", "at_risk", "critical"];
const errMsg = (err, fallback) => err?.message || fallback;
const tk = (pid, key) => `${pid}:${key}`;

// In-memory only (no storage, nothing persisted): unsent drafts, unsaved owner
// summaries and unresolved ambiguous-append locks survive closing and
// reopening the dialog within this page session, keyed to the server week.
// Bound to the authenticated linked person + server week: a different identity
// (sign-out/switch without reload) or week wipes it before anything restores.
const sessionMemory = { personId: null, week: null, drafts: {}, summaries: {}, ambiguous: {}, carried: {} };
export function __resetMyUpdateMemory() { sessionMemory.personId = null; sessionMemory.week = null; sessionMemory.drafts = {}; sessionMemory.summaries = {}; sessionMemory.ambiguous = {}; sessionMemory.carried = {}; }

export function MyWeeklyUpdate({ focusProjectId = null, onChanged, onPendingChange, currentRole = "viewer" }) {
  const roleRef = useRef(currentRole);
  roleRef.current = currentRole;
  const [base, setBase] = useState({ loading: true, error: null });
  const [week, setWeek] = useState(null);
  const [frozen, setFrozen] = useState(false);
  const [weekChanged, setWeekChanged] = useState(false);
  const [accessLost, setAccessLost] = useState(false);
  const [entries, setEntries] = useState([]);
  const [me, setMe] = useState(null);
  const [projects, setProjects] = useState({});
  const [prior, setPrior] = useState({});
  const [priorError, setPriorError] = useState(null);
  const [priorStatus, setPriorStatus] = useState({});
  const [participants, setParticipants] = useState({});
  const [drafts, setDrafts] = useState({});
  const [noteState, setNoteState] = useState({});
  const [statusState, setStatusState] = useState({});
  const [summary, setSummary] = useState({});
  const [carried, setCarried] = useState({});
  const carriedRef = useRef(carried);
  carriedRef.current = carried;
  const [completion, setCompletion] = useState({ busy: false, results: null });
  const [revalidated, setRevalidated] = useState(null);

  const gen = useRef(0);
  const queue = useRef(createSerialQueue());
  const guard = useRef(createAppendGuard());
  const summaryInit = useRef(new Set());
  const summaryTimers = useRef({});
  const summaryRef = useRef(summary);
  summaryRef.current = summary;
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const statusSeq = useRef({});
  // Explicit acknowledgements, not render timing, decide whether completion
  // may follow the queued saves. A rejected status cannot count as flushed.
  const saveOutcomes = useRef({});
  const completing = useRef(false);
  const inflight = useRef({});
  // Queue tasks re-check the generation/week before any network call so stale
  // work queued before a reload, week rollover or unmount never posts.
  const live = (g, wk) => g === gen.current && wk === weekRef.current;
  const weekRef = useRef(null);
  weekRef.current = week;
  const STALE = useRef(Object.freeze({ stale: true })).current;
  const enqueue = (pid, g, wk, fn) => queue.current.run(pid, () => (live(g, wk) ? fn() : Promise.reject(STALE)));

  const notify = () => { try { onChangedRef.current?.(); } catch { /* parent refresh only */ } };

  // Assemble current-week project models. Overview carries corrections and
  // capabilities; assigned projects absent from it (e.g. phase complete) fall
  // back to the assembled-week read and render read-only.
  const loadProjects = useCallback(async (cw, grouped) => {
    const ov = await getWeeklyOverview(cw);
    const byId = new Map((ov?.projects || []).map((p) => [p.id, p]));
    const out = {};
    await Promise.all(grouped.map(async (e) => {
      if (byId.has(e.projectId)) { out[e.projectId] = byId.get(e.projectId); return; }
      const wk = await getProjectWeek(e.projectId, cw);
      // Not in the overview (e.g. phase complete): capability derives from the
      // trusted assignment rows + app role + live current week; server re-checks.
      const canWrite = ["admin", "editor"].includes(roleRef.current) && !wk?.frozen && !ov?.frozen && (wk?.currentWeek ?? cw) === cw;
      out[e.projectId] = { id: e.projectId, name: e.projectName, lines: (wk?.lines || []).map((l) => ({ ...l, corrections: Array.isArray(l.corrections) ? l.corrections : [] })), statuses: wk?.statuses || [], summary: wk?.summary || null, completions: wk?.completions || [],
        capabilities: canWrite ? { canSetTrackStatus: [...e.trackKeys], canSummary: e.isOwner } : {}, outsideOverview: true, frozen: Boolean(wk?.frozen), priorSections: wk?.priorSections };
    }));
    return { out, frozen: Boolean(ov?.frozen) };
  }, []);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const g = ++gen.current;
    if (!quiet) { guard.current = createAppendGuard(); inflight.current = {}; }
    if (!quiet) setBase({ loading: true, error: null });
    try {
      const weeks = await getWeeklyWeeks();
      const cw = weeks?.currentWeek;
      if (!isMondayIso(cw)) throw new Error("The server did not report a current week.");
      const asg = await getMyAssignments();
      const grouped = groupAssignments(asg?.assignments);
      const { out, frozen: ovFrozen } = await loadProjects(cw, grouped);
      if (g !== gen.current) return;
      const who = myPersonId(asg?.assignments);
      if (who && sessionMemory.week === cw && sessionMemory.personId === who) {
        setDrafts(sessionMemory.drafts);
        setNoteState(Object.fromEntries(Object.entries(sessionMemory.ambiguous).map(([k, v]) => [k, v])));
        for (const k of Object.keys(sessionMemory.ambiguous)) guard.current.lock(k);
        for (const pid of Object.keys(sessionMemory.summaries)) summaryInit.current.add(pid);
        setSummary(sessionMemory.summaries);
        setCarried(sessionMemory.carried || {});
      } else {
        __resetMyUpdateMemory(); sessionMemory.personId = who; sessionMemory.week = cw;
        summaryInit.current.clear(); setSummary({}); setDrafts({}); setCarried({});
      }
      setWeek(cw);
      setWeekChanged(false);
      setAccessLost(false);
      setFrozen(ovFrozen || Boolean((weeks?.weeks || []).find((w) => w.weekDate === cw)?.frozen));
      setEntries(grouped);
      setMe(myPersonId(asg?.assignments));
      setProjects(out);
      setBase({ loading: false, error: null });
      // Already authorized, independently dated prior sections. No week loop
      // and no filtering to the current contributor's historical posts.
      setPrior(Object.fromEntries(grouped.map(e => [e.projectId, out[e.projectId]?.priorSections])));
      setPriorStatus(Object.fromEntries(grouped.map(e => [e.projectId, out[e.projectId]?.priorSections ? "ok" : "error"])));
      setPriorError(null);
      Promise.all(grouped.map((e) => getProjectParticipants(e.projectId)
        .then((r) => [e.projectId, r?.participants || []]).catch(() => [e.projectId, null])))
        .then((pairs) => { if (g === gen.current) setParticipants(Object.fromEntries(pairs)); });
    } catch (err) {
      if (g !== gen.current) return;
      setBase({ loading: false, error: errMsg(err, "Your weekly update could not be loaded.") });
    }
  }, [loadProjects]);

  useEffect(() => {
    load();
    // This is a generation counter, not a captured DOM ref; increment the
    // latest value to invalidate every outstanding response on unmount.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      gen.current++;
    };
  }, [load]);

  // Server summary seeds the owner zone exactly once per project so refetches
  // never overwrite in-progress typing.
  useEffect(() => {
    const next = {};
    for (const e of entries) {
      if (summaryInit.current.has(e.projectId) || !projects[e.projectId]) continue;
      summaryInit.current.add(e.projectId);
      const s = projects[e.projectId].summary;
      next[e.projectId] = { overall: s?.statusOverall || null, narrative: s?.narrative || "", state: "idle", error: null, dirty: false };
    }
    if (Object.keys(next).length) setSummary((cur) => ({ ...next, ...cur }));
  }, [entries, projects]);

  const refreshCurrent = useCallback(async () => {
    if (!week) return;
    const g = gen.current;
    try {
      const { out, frozen: f } = await loadProjects(week, entries);
      if (g !== gen.current) return null;
      setProjects(out);
      if (f) setFrozen(true);
      return out;
    } catch { return null; }
  }, [week, entries, loadProjects]);

  // After a refusal: check freeze and membership again. Drafts are untouched.
  const revalidate = useCallback(async (kind) => {
    try {
      const weeks = await getWeeklyWeeks();
      if ((weeks?.weeks || []).find((w) => w.weekDate === week)?.frozen) setFrozen(true);
      if (weeks?.currentWeek !== week) setWeekChanged(true);
    } catch { /* keep current view; error already shown */ }
    if (kind === WRITE_KIND.AUTH) {
      try {
        const asg = await getMyAssignments();
        setEntries(groupAssignments(asg?.assignments));
        setRevalidated("Your assignments changed. Controls now reflect your current access; unsaved drafts are kept.");
      } catch (err) {
        setAccessLost(true);
        setRevalidated(errMsg(err, "Your access could not be rechecked."));
      }
    }
    await refreshCurrent();
  }, [week, refreshCurrent]);
  // Keep drafts, but revoke old-week controls after a UTC rollover or resume.
  useEffect(() => {
    if (!week) return undefined;
    const check = () => { void revalidate(); };
    const timer = window.setInterval(check, 60_000);
    window.addEventListener("focus", check);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", check); };
  }, [week, revalidate]);

  // ── Note append ────────────────────────────────────────────────────────────
  // Returns a promise resolving true only when the line is acknowledged (or
  // there is nothing to send). A busy key returns the same in-flight promise so
  // completion waits for it; a locked (ambiguous) key resolves false.
  const appendNote = useCallback((pid, key, explicit = false) => {
    const k = tk(pid, key);
    if (carriedRef.current[k] && !explicit) return Promise.resolve(false);
    const text = (draftsRef.current[k] || "").trim();
    if (inflight.current[k]) return inflight.current[k];
    if (!text || !week) return Promise.resolve(true);
    if (!guard.current.tryBegin(k)) return Promise.resolve(false);
    const g = gen.current, wk = week;
    const knownIds = (projects[pid]?.lines || []).map((l) => l.id);
    setNoteState((s) => ({ ...s, [k]: { state: "saving", error: null, text, knownIds } }));
    // A request already sent can still commit after this dialog unmounts.
    // Preserve its uncertainty immediately, not just after a failed response.
    if (sessionMemory.personId === me && sessionMemory.week === wk) {
      sessionMemory.ambiguous[k] = {
        state: "ambiguous", text, knownIds,
        error: "The note save was still in progress. Check whether it saved before sending again.",
      };
    }
    const promise = enqueue(pid, g, wk, () => postTrackUpdate(pid, key, { weekDate: wk, note: text })).then((line) => {
      guard.current.end(k);
      // Blur can start a request just before Close unmounts this dialog.
      // The parent must refresh after the commit, even when this editor is gone.
      notify();
      if (!live(g, wk)) return false;
      setDrafts((d) => ((d[k] || "").trim() === text ? { ...d, [k]: "" } : d));
      setProjects((ps) => ps[pid] ? { ...ps, [pid]: { ...ps[pid], lines: [...(ps[pid].lines || []), { ...line, authorName: line.authorName || "You", corrections: [], revision: 0 }] } } : ps);
      setNoteState((s) => ({ ...s, [k]: { state: "saved", error: null } }));
      refreshCurrent();
      return true;
    }, async (err) => {
      guard.current.end(k);
      if (err === STALE || !live(g, wk)) return false;
      const kind = classifyWriteError(err);
      if (kind === WRITE_KIND.AMBIGUOUS) {
        // The original request may still commit after any read, so a missing
        // row never unlocks. Only a matching landed line clears the lock;
        // otherwise it stays locked; the user can only re-check (never resend).
        guard.current.lock(k);
        const fresh = await refreshCurrent();
        if (fresh && live(g, wk) && findLandedLine(fresh[pid], me, key, text, knownIds)) {
          guard.current.unlock(k);
          setDrafts((d) => ((d[k] || "").trim() === text ? { ...d, [k]: "" } : d));
          setNoteState((s) => ({ ...s, [k]: { state: "saved", error: null } }));
          notify();
          return true;
        }
        if (live(g, wk)) setNoteState((s) => ({ ...s, [k]: { state: "ambiguous", error: errMsg(err, "No response from the server."), text, knownIds } }));
        return false;
      }
      setNoteState((s) => ({ ...s, [k]: { state: "error", error: errMsg(err, "Could not save the note.") } }));
      revalidate(kind);
      return false;
    }).finally(() => { if (inflight.current[k] === promise) delete inflight.current[k]; });
    inflight.current[k] = promise;
    return promise;
  }, [week, projects, me, refreshCurrent, revalidate]); // eslint-disable-line react-hooks/exhaustive-deps

  const recheckAppend = useCallback(async (pid, key) => {
    const k = tk(pid, key);
    const st = noteState[k];
    if (!st || st.state !== "ambiguous") return;
    const fresh = await refreshCurrent();
    if (fresh && findLandedLine(fresh[pid], me, key, st.text, st.knownIds)) {
      guard.current.unlock(k);
      setDrafts((d) => ((d[k] || "").trim() === st.text ? { ...d, [k]: "" } : d));
      setNoteState((s) => ({ ...s, [k]: { state: "saved", error: null } }));
      notify();
    } else {
      setNoteState((s) => ({ ...s, [k]: { ...s[k], checkedAt: Date.now() } }));
    }
  }, [noteState, me, refreshCurrent]);  

  // ── Track status (shared; any assigned co-contributor) ───────────────────
  const setTrackStatus = (pid, key, status) => {
    const k = tk(pid, key);
    const seq = (statusSeq.current[k] || 0) + 1;
    statusSeq.current[k] = seq;
    const g = gen.current, wk = week;
    saveOutcomes.current[k] = false;
    setStatusState((s) => ({ ...s, [k]: { value: status, state: "saving", error: null } }));
    return enqueue(pid, g, wk, () => putTrackStatus(pid, key, { weekDate: wk, status })).then((saved) => {
      if (!live(g, wk) || statusSeq.current[k] !== seq) return true;
      saveOutcomes.current[k] = true;
      setProjects((ps) => {
        const p = ps[pid]; if (!p) return ps;
        const statuses = [...(p.statuses || []).filter((x) => x.trackKey !== key), { ...saved, trackKey: key, status, setterName: saved?.setterName || "you" }];
        return { ...ps, [pid]: { ...p, statuses } };
      });
      setStatusState((s) => ({ ...s, [k]: { value: status, state: "saved", error: null } }));
      notify();
      refreshCurrent();
      return true;
    }, (err) => {
      if (err === STALE || !live(g, wk) || statusSeq.current[k] !== seq) return false;
      setStatusState((s) => ({ ...s, [k]: { value: status, state: "error", error: errMsg(err, "Could not save the status.") } }));
      revalidate(classifyWriteError(err));
      return false;
    });
  };

  // ── Owner summary (overall + narrative share one PUT) ─────────────────────
  const saveSummary = useCallback((pid, explicit = false) => {
    clearTimeout(summaryTimers.current[pid]);
    const cur = summaryRef.current[pid];
    if (cur?.carried && !explicit) return Promise.resolve(false);
    if (!cur?.dirty) return Promise.resolve(cur?.state !== "error");
    if (!cur.overall) {
      setSummary((s) => ({ ...s, [pid]: { ...s[pid], state: "needs-status", error: null } }));
      return Promise.resolve(false);
    }
    const g = gen.current, wk = week;
    const sent = { overall: cur.overall, narrative: cur.narrative };
    saveOutcomes.current[tk(pid, "summary")] = false;
    setSummary((s) => ({ ...s, [pid]: { ...s[pid], state: "saving", error: null, dirty: false } }));
    return enqueue(pid, g, wk, () => putWeeklySummary(pid, { weekDate: wk, statusOverall: sent.overall, narrative: sent.narrative })).then(() => {
      if (!live(g, wk)) return false;
      saveOutcomes.current[tk(pid, "summary")] = true;
      setSummary((s) => {
        const now = s[pid];
        const changed = now.overall !== sent.overall || now.narrative !== sent.narrative;
        return { ...s, [pid]: { ...now, carried: changed ? now.carried : null, state: changed ? now.state : "saved", dirty: now.dirty || changed } };
      });
      notify();
      return true;
    }, (err) => {
      if (err === STALE || !live(g, wk)) return false;
      setSummary((s) => ({ ...s, [pid]: { ...s[pid], state: "error", error: errMsg(err, "Could not save the summary."), dirty: true } }));
      revalidate(classifyWriteError(err));
      return false;
    });
  }, [week, revalidate]); // eslint-disable-line react-hooks/exhaustive-deps

  const editSummary = (pid, patch) => {
    setSummary((s) => ({ ...s, [pid]: { ...s[pid], ...patch, dirty: true, state: s[pid]?.carried || patch.carried ? "draft" : "pending", error: null } }));
    clearTimeout(summaryTimers.current[pid]);
    if (!summaryRef.current[pid]?.carried && !patch.carried) summaryTimers.current[pid] = setTimeout(() => saveSummary(pid), SUMMARY_DEBOUNCE_MS);
  };

  // Unmount cancels pending timers; queued tasks see a new generation and never post.
  useEffect(() => () => { Object.values(summaryTimers.current).forEach(clearTimeout); }, []);
  useEffect(() => {
    if (!week || !me || sessionMemory.week !== week || sessionMemory.personId !== me) return;
    sessionMemory.drafts = drafts;
    sessionMemory.carried = carried;
    sessionMemory.summaries = Object.fromEntries(Object.entries(summary).filter(([, v]) => v?.dirty || v?.state === "error").map(([k, v]) => [k, { ...v, state: v.state === "saving" ? "pending" : v.state }]));
    sessionMemory.ambiguous = Object.fromEntries(Object.entries(noteState)
      .filter(([, v]) => ["ambiguous", "saving"].includes(v?.state))
      .map(([key, value]) => [key, value.state === "saving" ? {
        ...value, state: "ambiguous",
        error: "The note save was still in progress. Check whether it saved before sending again.",
      } : value]));
  }, [week, me, drafts, summary, noteState, carried]);

  // ── Completion: flush, then record self-completion per project ───────────
  const complete = async () => {
    if (completing.current) return;
    completing.current = true;
    setCompletion({ busy: true, results: null });
    const g = gen.current;
    await queue.current.drain();
    const results = [];
    for (const e of entries) {
      const p = projects[e.projectId];
      const writable = !frozen && !weekChanged && !accessLost && ["admin", "editor"].includes(currentRole) && !p?.frozen;
      const flushes = [];
      if (canEditSummary(e, p?.capabilities, writable)) flushes.push(saveSummary(e.projectId));
      for (const key of editableTrackKeys(e, p?.capabilities, writable)) flushes.push(appendNote(e.projectId, key));
      const flushed = (await Promise.all(flushes)).every(Boolean);
      await queue.current.drain();
      const ok = flushed && !Object.entries(saveOutcomes.current).some(([key, saved]) => key.startsWith(`${e.projectId}:`) && !saved);
      if (!ok) { results.push({ projectId: e.projectId, name: e.projectName, ok: false, error: "Some changes on this project are not saved yet. Fix them, then try again." }); continue; }
      try {
        await enqueue(e.projectId, g, week, () => postUpdateComplete(e.projectId, { weekDate: week }));
        results.push({ projectId: e.projectId, name: e.projectName, ok: true });
      } catch (err) {
        results.push({ projectId: e.projectId, name: e.projectName, ok: false, error: errMsg(err, "Could not mark complete.") });
      }
    }
    completing.current = false;
    if (g !== gen.current) return;
    setCompletion({ busy: false, results });
    if (results.some((r) => r.ok)) {
      showToast("Update posted", { type: "success" });
      notify();
      refreshCurrent();
    }
    if (results.some((r) => !r.ok)) revalidate(WRITE_KIND.REJECTED);
  };

  const ordered = useMemo(() => {
    if (!focusProjectId) return entries;
    return [...entries].sort((a, b) => (a.projectId === focusProjectId ? -1 : b.projectId === focusProjectId ? 1 : 0));
  }, [entries, focusProjectId]);

  const focusRef = useRef(null);
  useEffect(() => { if (focusProjectId && focusRef.current?.scrollIntoView) focusRef.current.scrollIntoView({ block: "start" }); }, [focusProjectId, base.loading]);

  const anySaving = Object.values(noteState).some((s) => s.state === "saving")
    || Object.values(statusState).some((s) => s.state === "saving")
    || Object.values(summary).some((s) => s?.state === "saving" || s?.state === "pending");
  const anyError = Object.values(noteState).some((s) => s.state === "error" || s.state === "ambiguous")
    || Object.values(statusState).some((s) => s.state === "error")
    || Object.values(summary).some((s) => s?.state === "error" || s?.state === "needs-status");

  const pendingRef = useRef(onPendingChange);
  pendingRef.current = onPendingChange;
  const hasCarriedDraft = Object.keys(carried).length > 0 || Object.values(summary).some(s => s.carried);
  useEffect(() => { pendingRef.current?.(anySaving || hasCarriedDraft); }, [anySaving, hasCarriedDraft]);

  if (base.loading) {
    return (
      <div data-testid="my-update-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 18 }}>
        {[48, 120, 120].map((h, i) => <div key={i} style={{ height: h, borderRadius: 10, background: T.lightGray }} />)}
      </div>
    );
  }
  if (base.error) {
    return (
      <div role="alert" data-testid="my-update-error" style={{ margin: 18, padding: "10px 14px", borderRadius: 8, background: T.utilRed, border: `1px solid ${T.redRing}`, display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ flex: 1, fontSize: T.fsSm, color: T.dark }}>{base.error}</span>
        <button type="button" style={btn} data-testid="my-update-retry" onClick={() => load()}>Retry</button>
      </div>
    );
  }

  return (
    <section data-testid="my-weekly-update" aria-label="My weekly update" style={{ padding: "16px 20px 20px", background: "var(--v2-tint)" }}>
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".1em", textTransform: "uppercase", color: T.mid }}>Your weekly update</div>
      <div style={{ fontSize: T.fsLg || T.fsBase, fontWeight: T.fwBold, color: T.dark, margin: "2px 0" }}>Where does it stand?</div>
      <div data-testid="my-update-week" style={{ ...muted, fontSize: T.fsSm, marginBottom: 12 }}>
        <strong style={{ color: T.dark }}>{weekLabel(week)}</strong> · {weekChanged ? "previous week (server UTC)" : "current week (server UTC)"} · {entries.length} project{entries.length === 1 ? "" : "s"}
        {frozen ? " · finalized, read-only" : weekChanged || accessLost || !["admin", "editor"].includes(currentRole) ? " · read-only" : " · saves automatically, editable until the week is frozen"}
      </div>

      {frozen && (
        <div role="status" data-testid="my-update-frozen" style={{ padding: "9px 12px", borderRadius: 8, background: T.lightGray, color: T.dark, fontSize: T.fsSm, marginBottom: 12 }}>
          This week has been frozen. Everything is read-only; any unsent drafts stay visible below so you can copy them.
        </div>
      )}
      {revalidated && <div role="status" data-testid="my-update-revalidated" style={{ ...muted, fontSize: T.fsSm, marginBottom: 10 }}>{revalidated}</div>}
      {weekChanged && <div role="status" style={{ ...muted, marginBottom: 10 }}>The server's current week has changed. Reopen My weekly update for the new week. Unsent drafts remain visible so you can copy them.</div>}
      {priorError && <div role="status" data-testid="my-update-prior-error" style={{ ...muted, marginBottom: 10 }}>Last week unavailable: {priorError}</div>}

      {entries.length === 0 && (
        <div data-testid="my-update-empty" style={{ border: `1px dashed ${T.border}`, borderRadius: 12, padding: "22px 20px", background: T.card }}>
          <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>You are not assigned to any project yet</div>
          <p style={{ margin: 0, ...muted, fontSize: T.fsSm }}>Updates come from project assignments. An admin can add you under Team.</p>
        </div>
      )}

      {ordered.map((e) => (
        <div key={e.projectId} ref={e.projectId === focusProjectId ? focusRef : undefined}>
          <ProjectUpdateCard
            entry={e} project={projects[e.projectId]} priorProject={prior[e.projectId]} priorState={priorStatus[e.projectId] || "loading"}
            participants={participants[e.projectId]} me={me} frozen={frozen || weekChanged || accessLost || !["admin", "editor"].includes(currentRole)} week={week} focused={e.projectId === focusProjectId}
            drafts={drafts} setDraft={(key, v) => setDrafts((d) => ({ ...d, [tk(e.projectId, key)]: v }))}
            carried={carried}
            onCarry={(key, source) => {
              const k = tk(e.projectId, key);
              setDrafts(d => ({ ...d, [k]: source.text }));
              setCarried(c => ({ ...c, [k]: source }));
            }}
            onCancelCarry={key => {
              const k = tk(e.projectId, key);
              setDrafts(d => ({ ...d, [k]: "" }));
              setCarried(c => { const next = { ...c }; delete next[k]; return next; });
            }}
            onPostCarry={async key => {
              const k = tk(e.projectId, key), source = carriedRef.current[k];
              if (!await appendNote(e.projectId, key, true)) return;
              if (source?.status && !await setTrackStatus(e.projectId, key, source.status)) return;
              setCarried(c => { const next = { ...c }; delete next[k]; return next; });
            }}
            noteState={noteState} statusState={statusState} summary={summary[e.projectId]}
            onAppend={(key) => appendNote(e.projectId, key)} onRecheck={(key) => recheckAppend(e.projectId, key)} onStatus={(key, s) => {
              const k = tk(e.projectId, key);
              if (carriedRef.current[k]) setCarried(c => ({ ...c, [k]: { ...c[k], status: s } }));
              else setTrackStatus(e.projectId, key, s);
            }}
            onSummary={(patch) => editSummary(e.projectId, patch)} onSummaryFlush={() => saveSummary(e.projectId)}
            onSummaryPost={() => saveSummary(e.projectId, true)}
            onSummaryCancel={() => {
              clearTimeout(summaryTimers.current[e.projectId]);
              const saved = projects[e.projectId]?.summary;
              setSummary(s => ({ ...s, [e.projectId]: { overall: saved?.statusOverall || null, narrative: saved?.narrative || "", state: "idle", dirty: false, error: null } }));
            }}
            appendLocked={(key) => guard.current.isLocked(tk(e.projectId, key))}
            onCorrected={() => { notify(); refreshCurrent(); }} onCorrectionError={(err) => revalidate(classifyWriteError(err))}
          />
        </div>
      ))}

      {entries.length > 0 && (
        <CompletionBar
          busy={completion.busy} results={completion.results} frozen={frozen || weekChanged || accessLost || !["admin", "editor"].includes(currentRole)}
          saveLabel={anySaving ? "Saving…" : anyError ? "Some changes are not saved" : "All changes saved"} anyError={anyError}
          onComplete={complete} onKeepEditing={() => setCompletion({ busy: false, results: null })}
        />
      )}
    </section>
  );
}

function SaveTag({ state, testId }) {
  const map = { saving: "Saving…", pending: "Unsaved", ambiguous: "Unconfirmed", saved: "Saved" };
  if (!map[state]) return null;
  return <span data-testid={testId} style={{ ...muted, fontSize: T.fsXxs, color: state === "saved" ? T.green : T.mid }}>{map[state]}</span>;
}

function InlineError({ message, testId }) {
  if (!message) return null;
  return <div role="alert" data-testid={testId} style={{ color: T.red, fontSize: T.fsXs, marginTop: 4 }}>{message}</div>;
}

function ProjectUpdateCard({ entry, project, priorProject, priorState, participants, me, frozen, week, focused, drafts, setDraft, carried, onCarry, onCancelCarry, onPostCarry, noteState, statusState, summary, onAppend, onRecheck, onStatus, onSummary, onSummaryFlush, onSummaryPost, onSummaryCancel, appendLocked, onCorrected, onCorrectionError }) {
  const writable = !frozen && !project?.frozen;
  const editable = editableTrackKeys(entry, project?.capabilities, writable);
  const summaryEditable = canEditSummary(entry, project?.capabilities, writable);
  const mine = TRACKS.filter((t) => entry.trackKeys.includes(t.key));
  const others = TRACKS.filter((t) => !entry.trackKeys.includes(t.key));
  const suggestion = suggestOverall(project?.statuses);
  const done = mine.filter((t) => statusForTrack(project, t.key)).length;
  const completed = isCompletedBy(project, me);
  const roleText = entry.isOwner
    ? `You: Owner${mine.length ? ` + ${mine.map((t) => t.label).join(", ")}` : ""}`
    : `You: ${mine.map((t) => t.label).join(", ")}`;
  const participantsFailed = participants === null;
  const coNames = (key) => (participants || []).filter((a) => a.trackKey === key && a.personId !== me).map((a) => a.displayName).filter(Boolean);

  const trackRow = (t, canEdit) => (
    <TrackRow
      key={t.key} project={project} track={t} canEdit={canEdit} mine={entry.trackKeys.includes(t.key)} me={me} week={week}
      co={coNames(t.key)} draft={drafts[tk(entry.projectId, t.key)] || ""} setDraft={(v) => setDraft(t.key, v)}
      note={noteState[tk(entry.projectId, t.key)]} status={statusState[tk(entry.projectId, t.key)]}
      onAppend={() => onAppend(t.key)} onRecheck={() => onRecheck(t.key)} onStatus={(s) => onStatus(t.key, s)} locked={appendLocked(t.key)}
      priorSource={priorProject?.tracks?.[t.key]} priorState={priorState}
      carried={carried[tk(entry.projectId, t.key)]} onCarry={source => onCarry(t.key, source)}
      onCancelCarry={() => onCancelCarry(t.key)} onPostCarry={() => onPostCarry(t.key)}
      onCorrected={onCorrected} onCorrectionError={onCorrectionError}
    />
  );

  return (
    <article data-testid={`my-update-card-${entry.projectId}`} aria-label={entry.projectName} style={{ background: T.card, border: `1px solid ${focused ? T.blue : T.border}`, borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>{entry.projectName}</div>
          {project?.city && <div style={muted}>{project.city}{project.region ? ` · ${project.region}` : ""}</div>}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
          {completed && <span data-testid={`my-update-completed-${entry.projectId}`} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.green, background: T.greenSoft, borderRadius: 999, padding: "2px 9px" }}>Marked complete</span>}
          <span data-testid={`my-update-role-${entry.projectId}`} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, borderRadius: 999, padding: "3px 10px", background: entry.isOwner ? T.blueSoft : T.lightGray, color: entry.isOwner ? T.blue : T.dark }}>{roleText}</span>
        </div>
      </header>

      {participantsFailed && <p role="status" data-testid={`my-update-participants-error-${entry.projectId}`} style={{ ...muted, marginTop: 0 }}>Co-contributors could not be loaded; shared-track names may be missing.</p>}
      {project?.outsideOverview && <p data-testid={`my-update-outside-${entry.projectId}`} style={{ ...muted, fontSize: T.fsSm, marginTop: 0 }}>This project is not in this week's Weekly Review (for example, it is complete). Your assigned tracks remain editable here.</p>}

      {entry.isOwner && (
        <OwnerZone pid={entry.projectId} project={project} summary={summary} editable={summaryEditable} suggestion={suggestion} onSummary={onSummary} onFlush={onSummaryFlush} onPost={onSummaryPost} onCancel={onSummaryCancel} />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4 }}>
        <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark }}>{entry.isOwner ? "All tracks" : mine.length > 1 ? "Your tracks" : "Your track"}</span>
        {mine.length > 0 && <span data-testid={`my-update-progress-${entry.projectId}`} style={{ fontSize: T.fsXs, color: T.green, fontWeight: T.fwBold }}>{entry.isOwner ? `your ${done} of ${mine.length} tracks have a status` : `${done} of ${mine.length} have a status`}</span>}
      </div>

      {entry.isOwner ? TRACKS.map((t) => trackRow(t, editable.includes(t.key))) : (
        <>
          {mine.map((t) => trackRow(t, editable.includes(t.key)))}
          {others.length > 0 && (
            <details data-testid={`my-update-others-${entry.projectId}`} style={{ marginTop: 10, borderTop: `1px dashed ${T.border}`, paddingTop: 8 }}>
              <summary style={{ cursor: "pointer", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue }}>Other tracks on this project ({others.length}) · for context</summary>
              {others.map((t) => trackRow(t, false))}
            </details>
          )}
        </>
      )}
    </article>
  );
}

function OwnerZone({ pid, project, summary, editable, suggestion, onSummary, onFlush, onPost, onCancel }) {
  const s = summary || { overall: project?.summary?.statusOverall || null, narrative: project?.summary?.narrative || "" };
  return (
    <div data-testid={`my-update-owner-${pid}`} style={{ background: T.blueSoft, borderRadius: 11, padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Overall this week</span>
        <div role="radiogroup" aria-label="Overall this week" style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", background: T.card }}>
          {OVERALL_ORDER.map((v) => {
            const on = s.overall === v, m = OVERALL_STATUS_META[v];
            return (
              <button key={v} type="button" role="radio" aria-checked={on} disabled={!editable} data-testid={`my-update-overall-${pid}-${v}`}
                onClick={() => onSummary({ overall: v })}
                style={{ border: "none", borderRight: `1px solid ${T.border}`, padding: "6px 12px", fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: editable ? "pointer" : "default", background: on ? m.bg : T.card, color: on ? m.color : T.mid }}>
                {m.label}
              </button>
            );
          })}
        </div>
        {suggestion && (
          <span data-testid={`my-update-suggestion-${pid}`} style={{ fontSize: T.fsXs, color: T.ragAmberText, fontWeight: T.fwSemibold }}>
            Suggested: {OVERALL_STATUS_META[suggestion.value].label} ({suggestion.reason})
            {editable && s.overall !== suggestion.value && (
              <button type="button" data-testid={`my-update-apply-suggestion-${pid}`} onClick={() => onSummary({ overall: suggestion.value })} style={{ ...btn, marginLeft: 6, padding: "1px 8px" }}>Use suggestion</button>
            )}
          </span>
        )}
        <SaveTag state={summary?.state} testId={`my-update-summary-state-${pid}`} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
        <label htmlFor={`my-update-narrative-${pid}`} style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue }}>Project summary this week</label>
        {editable && (
          <button type="button" data-testid={`my-update-draft-${pid}`} disabled={!draftFromTracks(project)} title={draftFromTracks(project) ? "Prefill from this week's track lines" : "No track lines this week yet"}
            onClick={() => onSummary({ narrative: draftFromTracks(project) })} style={withDisabled({ ...btn, color: T.blue, borderColor: T.blue }, !draftFromTracks(project))}>
            Draft from track updates
          </button>
        )}
      </div>
      {editable ? (
        <>
          <CarryForward source={summary?.carried || project?.priorSections?.summary?.narrative} value={s.narrative} savedValue={project?.summary?.narrative} carried={Boolean(summary?.carried)} testId={`my-update-summary-carry-${pid}`} onCarry={source => onSummary({ narrative: source.text, carried: source })} />
          {summary?.carried
            ? <WeeklyRichEditor aria-label="Project summary this week" data-testid={`my-update-narrative-${pid}`} value={s.narrative} disabled={summary.state === "saving"} onChange={value => onSummary({ narrative: value })} />
            : <textarea id={`my-update-narrative-${pid}`} data-testid={`my-update-narrative-${pid}`} value={weeklyText(s.narrative)} rows={3}
              placeholder="Your synthesis across tracks for this project…" onChange={(e) => onSummary({ narrative: e.target.value })} onBlur={onFlush}
              style={{ ...inputStyle, resize: "vertical", background: T.card }} />}
          {summary?.carried && <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button type="button" data-testid={`my-update-summary-post-${pid}`} disabled={summary.state === "saving"} onClick={onPost} style={btnPrimary}>Post</button>
            <button type="button" disabled={summary.state === "saving"} onClick={onCancel} style={btn}>Cancel</button>
          </div>}
        </>
      ) : (
        <p data-testid={`my-update-narrative-ro-${pid}`} style={{ margin: 0, fontSize: T.fsSm, color: s.narrative ? T.dark : T.light, fontStyle: s.narrative ? "normal" : "italic" }}>{weeklyText(s.narrative) || "No summary yet this week."}</p>
      )}
      {summary?.state === "needs-status" && <div role="alert" data-testid={`my-update-summary-needs-${pid}`} style={{ color: T.ragAmberText, fontSize: T.fsXs, marginTop: 4 }}>Choose an overall status to save the summary. Nothing is selected for you.</div>}
      <InlineError message={summary?.error} testId={`my-update-summary-error-${pid}`} />
      <div style={{ ...muted, fontSize: T.fsXxs, marginTop: 4 }}>Owner-only. Separate from track notes.</div>
    </div>
  );
}

function TrackRow({ project, track, canEdit, mine, me, week, co, draft, setDraft, note, status, onAppend, onRecheck, onStatus, locked, priorSource, priorState, carried, onCarry, onCancelCarry, onPostCarry, onCorrected, onCorrectionError }) {
  const pid = project?.id;
  const server = statusForTrack(project, track.key);
  const shown = carried?.status || (status?.state === "saving" || status?.state === "error" ? status.value : server?.status);
  const lines = (project?.lines || []).filter((l) => l.trackKey === track.key);
  const busy = note?.state === "saving";
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onAppend(); } };

  return (
    <div data-testid={`my-update-track-${pid}-${track.key}`} style={{ padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      {lines.length > 0 && (
        <ul style={{ listStyle: "none", margin: "0 0 7px", padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          {lines.map((l) => <AuthoredLine key={l.id} line={l} pid={pid} trackKey={track.key} canCorrect={canEdit && l.authorPersonId === me && Number.isInteger(l.revision)} week={week} onCorrected={onCorrected} onError={onCorrectionError} />)}
        </ul>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ width: 140, flexShrink: 0 }}>
          <div style={{ fontWeight: T.fwBold, fontSize: T.fsSm, color: T.dark }}>{track.label}</div>
          {mine && <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: co.length ? T.blue : T.green }}>{co.length ? `with ${co.join(", ")}` : "you own"}</div>}
        </div>
        {canEdit ? (
          <div role="radiogroup" aria-label={`${track.label} status`} style={{ display: "inline-flex", gap: 4 }}>
            {TRACK_STATUS_ORDER.map((v) => {
              const m = TRACK_STATUS_META[v], on = shown === v;
              return (
                <button key={v} type="button" role="radio" aria-checked={on} data-testid={`my-update-status-${pid}-${track.key}-${v}`} onClick={() => onStatus(v)}
                  style={{ border: `1px solid ${on ? "transparent" : T.border}`, borderRadius: 7, padding: "4px 9px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", background: on ? m.bg : T.card, color: on ? m.color : T.mid }}>
                  {m.label}
                </button>
              );
            })}
          </div>
        ) : (
          <span data-testid={`my-update-status-ro-${pid}-${track.key}`} style={{ fontSize: T.fsXs, fontWeight: T.fwBold, borderRadius: 7, padding: "4px 10px", background: shown ? TRACK_STATUS_META[shown].bg : T.lightGray, color: shown ? TRACK_STATUS_META[shown].color : T.light }}>
            {shown ? TRACK_STATUS_META[shown].label : "No status"}
          </span>
        )}
        <SaveTag state={status?.state} testId={`my-update-status-state-${pid}-${track.key}`} />
        {canEdit ? (
          carried ? <WeeklyRichEditor
            data-testid={`my-update-note-${pid}-${track.key}`} aria-label={`${track.label} update`} value={draft} disabled={busy || locked} onChange={setDraft}
          /> : <input
            data-testid={`my-update-note-${pid}-${track.key}`} aria-label={`${track.label} update`} value={draft} disabled={busy} readOnly={locked}
            placeholder={co.length ? "Add your update… (Enter to post)" : "Add a note… (Enter to post)"}
            onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => { if (draft.trim() && !locked) onAppend(); }}
            style={{ ...inputStyle, flex: 1, minWidth: 180 }}
          />
        ) : (!lines.length && <span style={{ flex: 1, fontSize: T.fsXs, color: T.light, fontStyle: "italic" }}>No update yet this week</span>)}
        {canEdit && <SaveTag state={note?.state} testId={`my-update-note-state-${pid}-${track.key}`} />}
      </div>
      {!canEdit && draft && <div data-testid={`my-update-retained-${pid}-${track.key}`} style={{ ...muted, marginTop: 4 }}>Unsent draft (not saved): {draft}</div>}
      <InlineError message={status?.error} testId={`my-update-status-error-${pid}-${track.key}`} />
      <InlineError message={note?.error} testId={`my-update-note-error-${pid}-${track.key}`} />
      {locked && note?.state === "ambiguous" && (
        <div data-testid={`my-update-ambiguous-${pid}-${track.key}`} style={{ ...muted, marginTop: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span>We could not confirm this note was saved, and the original request may still arrive, so it will not be sent again from here. Your text is kept below; copy it if needed and check Weekly Review later.{note.checkedAt ? " Still not visible." : ""}</span>
          <button type="button" style={btn} data-testid={`my-update-recheck-${pid}-${track.key}`} onClick={onRecheck}>Check again</button>
        </div>
      )}
      {server?.setterName && <div data-testid={`my-update-setby-${pid}-${track.key}`} style={{ ...muted, fontSize: T.fsXxs, marginTop: 4 }}>status set by {server.setterName}{server.setAt ? ` · ${fmtTimestamp(server.setAt)}` : ""}{canEdit && mine ? " · you can change it" : ""}</div>}
      {mine && canEdit && (
        <div data-testid={`my-update-prior-${pid}-${track.key}`} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 6, fontSize: T.fsXs, color: T.mid }}>
          {priorState !== "ok" ? <span data-testid={`my-update-prior-state-${pid}-${track.key}`}>Prior posts could not be read.</span> : <CarryForward source={carried || priorSource} value={draft} savedValue={lines.filter(l => !resolveLine(l).struck).map(l => resolveLine(l).current).join("\n")} carried={Boolean(carried)} disabled={busy || locked} testId={`my-update-carry-${pid}-${track.key}`} onCarry={onCarry} />}
          {carried && <>
            <button type="button" data-testid={`my-update-post-${pid}-${track.key}`} disabled={busy || locked} onClick={onPostCarry} style={btnPrimary}>Post</button>
            <button type="button" disabled={busy || locked} onClick={onCancelCarry} style={btn}>Cancel</button>
          </>}
        </div>
      )}
    </div>
  );
}

function AuthoredLine({ line, pid, trackKey, canCorrect, week, onCorrected, onError }) {
  const r = resolveLine(line);
  const [mode, setMode] = useState(false);
  const [text, setText] = useState("");
  const [state, setState] = useState({ busy: false, error: null });
  const busyRef = useRef(false);
  const submit = async (action) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setState({ busy: true, error: null });
    try {
      await postLineCorrection(pid, trackKey, line.id, { weekDate: week, revision: line.revision, action, replacement: action === "replace" ? text : undefined });
      setMode(false);
      setState({ busy: false, error: null });
      onCorrected();
    } catch (err) {
      setState({ busy: false, error: err?.message || "Correction failed." });
      onError(err);
    } finally { busyRef.current = false; }
  };
  return (
    <li data-testid={`my-update-line-${line.id}`} style={{ fontSize: T.fsXs, color: T.dark, background: "var(--v2-tint)", borderRadius: 8, padding: "5px 10px" }}>
      <b style={{ color: T.dark }}>{line.authorName || "Unattributed"}</b>{line.authorOrg ? <span style={muted}> · {line.authorOrg}</span> : null}
      {line.createdAt && <span style={{ ...muted, marginLeft: 4 }}>{fmtTimestamp(line.createdAt)}</span>}{" "}
      {r.corrected ? <><s style={{ color: T.mid }}>{r.original}</s>{r.current ? ` ${r.current}` : ""}{r.struck && <span style={muted}> (struck)</span>}</> : r.original}
      {canCorrect && !mode && !r.struck && (
        <span style={{ marginLeft: 6, display: "inline-flex", gap: 4 }}>
          <button type="button" style={btn} data-testid={`my-update-line-correct-${line.id}`} onClick={() => { setMode(true); setText(r.current || r.original); }}>Correct</button>
          <button type="button" style={withDisabled(btn, state.busy)} disabled={state.busy} data-testid={`my-update-line-strike-${line.id}`} onClick={() => submit("strike")}>Strike</button>
        </span>
      )}
      {mode && (
        <span style={{ display: "flex", gap: 4, marginTop: 4 }}>
          <input aria-label="Corrected text" data-testid={`my-update-line-input-${line.id}`} value={text} onChange={(e) => setText(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
          <button type="button" style={withDisabled(btnPrimary, state.busy || !text.trim())} disabled={state.busy || !text.trim()} data-testid={`my-update-line-save-${line.id}`} onClick={() => submit("replace")}>Save</button>
          <button type="button" style={btn} onClick={() => setMode(false)}>Cancel</button>
        </span>
      )}
      <InlineError message={state.error} testId={`my-update-line-error-${line.id}`} />
    </li>
  );
}

function CompletionBar({ busy, results, frozen, saveLabel, anyError, onComplete, onKeepEditing }) {
  const succeeded = (results || []).filter((r) => r.ok);
  const failed = (results || []).filter((r) => !r.ok);
  if (results && failed.length === 0 && succeeded.length > 0) {
    return (
      <div data-testid="my-update-confirm" role="status" style={{ background: T.greenSoft, borderRadius: 14, padding: 18, textAlign: "center", marginTop: 6 }}>
        <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.green }}>You're in for the week.</div>
        <div style={{ fontSize: T.fsSm, color: T.green, marginTop: 4 }}>Marked complete on {succeeded.map((r) => r.name).join(", ")}. You can keep editing until the week is frozen.</div>
        <button type="button" style={{ ...btn, marginTop: 10 }} data-testid="my-update-keep-editing" onClick={onKeepEditing}>Keep editing</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6 }}>
      {failed.length > 0 && (
        <div role="alert" data-testid="my-update-complete-partial" style={{ padding: "10px 12px", borderRadius: 8, background: T.utilRed, border: `1px solid ${T.redRing}`, marginBottom: 10, fontSize: T.fsSm }}>
          {succeeded.length > 0 && <div style={{ color: T.green, marginBottom: 4 }}>Marked complete: {succeeded.map((r) => r.name).join(", ")}.</div>}
          {failed.map((r) => <div key={r.projectId} data-testid={`my-update-complete-failed-${r.projectId}`}><strong>{r.name}:</strong> {r.error}</div>)}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <span data-testid="my-update-save-label" style={{ ...muted, fontSize: T.fsSm, color: anyError ? T.red : T.mid }}>{saveLabel}</span>
        {!frozen && (
          <button type="button" data-testid="my-update-complete" disabled={busy} onClick={onComplete} style={withDisabled({ ...btnPrimary, padding: "9px 16px", fontSize: T.fsSm }, busy)}>
            {busy ? "Saving and marking…" : failed.length ? "Try again" : "Mark my update complete for the week"}
          </button>
        )}
      </div>
    </div>
  );
}
